import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { updatesubscriptionplane } from "../../Api.jsx";
export default function Modal({ user, onClose, onSuccess }) {
  const [planName, setPlanName] = useState("");
  const [timePeriod, setTimePeriod] = useState("");
  const [price, setPrice] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [description, setDescription] = useState("");
  const [features, setFeatures] = useState([""]); // Changed to array of strings
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [appStoreProductId, setAppStoreProductId] = useState("");
  const [isManualId, setIsManualId] = useState(false);
  const [autoAssignDietChart, setAutoAssignDietChart] = useState(false);
  const [errors, setErrors] = useState({});
  const PlanID = parseInt(user?.id, 10);

  const validateForm = () => {
    const newErrors = {};
    if (!planName.trim()) newErrors.planName = 'Plan Name is required';
    if (!timePeriod) newErrors.timePeriod = 'Time Period is required';
    if (timePeriod === 'CustomDays') {
      const days = Number(durationDays);
      if (!durationDays || Number.isNaN(days) || days <= 0) {
        newErrors.durationDays = 'Duration days must be greater than 0';
      }
    }
    if (!price) newErrors.price = 'Price is required';
    if (!description.trim()) newErrors.description = 'Description is required';

    const hasValidFeature = features.some(f => f.trim() !== '');
    if (!hasValidFeature) newErrors.features = 'At least one feature is required';
    if (!appStoreProductId.trim()) newErrors.appStoreProductId = 'App Store Product ID is required';
    else if (!appStoreProductId.startsWith('gym_')) newErrors.appStoreProductId = 'Must start with gym_';

    if (!appStoreProductId.trim()) newErrors.appStoreProductId = 'App Store Product ID is required';
    else if (!appStoreProductId.startsWith('gym_')) newErrors.appStoreProductId = 'Must start with gym_';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-generate App Store Product ID
  useEffect(() => {
    if (!isManualId || !appStoreProductId) {
      const name = planName.toLowerCase().replace(/\s+/g, '_');
      const duration = timePeriod === "CustomDays" && durationDays
        ? `${durationDays}days`
        : timePeriod.toLowerCase();
      if (name && duration) {
        setAppStoreProductId(`gym_${name}_${duration}`);
        setIsManualId(false); // Reset manual flag if we are auto-generating because it was empty
      }
    }
  }, [planName, timePeriod, durationDays, isManualId, appStoreProductId]);
  useEffect(() => {
    if (user) {
      setPlanName(user.planName || "");
      setTimePeriod(user.timePeriod || "Monthly");
      setPrice(user.price || "");
      setDurationDays(user.durationDays || "");
      setDescription(user.description || "");
      // Convert features to array format
      if (user.features) {
        if (Array.isArray(user.features)) {
          setFeatures(user.features.length > 0 ? user.features : [""]);
        } else {
          const featuresArray = user.features.split('\n').filter(feature => feature.trim() !== '');
          setFeatures(featuresArray.length > 0 ? featuresArray : [""]);
        }
      } else {
        setFeatures([""]);
      }
      setDiscountPercentage(user.discountPercentage || "");
      setAppStoreProductId(user.appStoreProductId || "");
      setAutoAssignDietChart(user.autoAssignDietChart || false);

      // Smart Auto-sync: Check if existing ID matches the auto-generated pattern
      const expectedId = `gym_${(user.planName || "").toLowerCase().replace(/\s+/g, '_')}_${(user.timePeriod || "Monthly").toLowerCase()}`;
      if (user.appStoreProductId && user.appStoreProductId !== expectedId) {
        setIsManualId(true); // Custom ID, protect it
      } else {
        setIsManualId(false); // standard or empty ID, allow sync
      }
    }
  }, [user]);
  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      // Filter out empty features
      const featuresArray = features.filter(feature => feature.trim() !== '');
      const result = await updatesubscriptionplane(
        PlanID,
        planName,
        timePeriod,
        timePeriod === 'CustomDays' ? Number(durationDays) : null,
        price,
        description,
        featuresArray,
        discountPercentage,
        appStoreProductId,
        autoAssignDietChart
      );
      if (result.status === 200) {
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Failed to save data:", error);
    }
  };
  // Function to handle feature input change
  const handleFeatureChange = (index, value) => {
    const newFeatures = [...features];
    newFeatures[index] = value;
    setFeatures(newFeatures);
  };
  // Function to add a new feature input
  const addFeature = () => {
    setFeatures([...features, ""]);
  };
  // Function to remove a feature input
  const removeFeature = (index) => {
    if (features.length > 1) {
      const newFeatures = [...features];
      newFeatures.splice(index, 1);
      setFeatures(newFeatures);
    }
  };
  const timePeriodOptions = ["Monthly", "Quarterly", "HalfYearly", "Yearly", "CustomDays"];
  return (
    <AnimatePresence>
      {user && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="relative w-full max-w-3xl mx-4 rounded-2xl bg-white shadow-2xl border border-gray-200 max-h-[92vh] flex flex-col"
            initial={{ scale: 0.95, y: 10 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 10 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-100 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Update Subscription Plan</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSave}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow hover:shadow-md transition-all duration-200"
                  >
                    Save
                  </button>
                  <button
                    onClick={onClose}
                    className="rounded-md p-1 hover:bg-gray-100 text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-y-auto px-6 py-4 space-y-4">
              <div className="space-y-3">
                {/* Plan Name */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={planName}
                    placeholder="Enter plan name"
                    onChange={(e) => {
                      setPlanName(e.target.value);
                      if (errors.planName) setErrors({ ...errors, planName: null });
                    }}
                    className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.planName ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.planName && <p className="text-red-500 text-xs mt-1">{errors.planName}</p>}
                </div>
                {/* App Store Product ID */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    App Store Product ID * (Convention: gym_[plan_name]_[duration])
                  </label>
                  <input
                    type="text"
                    value={appStoreProductId}
                    placeholder="e.g., gym_basic_monthly"
                    onChange={(e) => {
                      setAppStoreProductId(e.target.value);
                      setIsManualId(true);
                      if (errors.appStoreProductId) setErrors({ ...errors, appStoreProductId: null });
                    }}
                    className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.appStoreProductId ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {errors.appStoreProductId && <p className="text-red-500 text-xs mt-1">{errors.appStoreProductId}</p>}
                </div>
                {/* Time Period, Price & Discount Percentage */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Time Period *
                    </label>
                    <select
                      value={timePeriod}
                      onChange={(e) => {
                        setTimePeriod(e.target.value);
                        if (errors.timePeriod) setErrors({ ...errors, timePeriod: null });
                      }}
                      className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.timePeriod ? 'border-red-500' : 'border-gray-300'}`}
                    >
                      <option value="" disabled>Select</option>
                      {timePeriodOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    {errors.timePeriod && <p className="text-red-500 text-xs mt-1">{errors.timePeriod}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Duration Days
                    </label>
                    <input
                      type="number"
                      value={durationDays}
                      placeholder="e.g. 7 or 14"
                      onChange={(e) => {
                        setDurationDays(e.target.value);
                        if (errors.durationDays) setErrors({ ...errors, durationDays: null });
                      }}
                      disabled={timePeriod !== 'CustomDays'}
                      className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 ${errors.durationDays ? 'border-red-500' : 'border-gray-300'}`}
                      min="1"
                      step="1"
                    />
                    {errors.durationDays && <p className="text-red-500 text-xs mt-1">{errors.durationDays}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Price (INR)*
                    </label>
                    <input
                      type="number"
                      value={price}
                      placeholder="Price"
                      onChange={(e) => {
                        setPrice(e.target.value);
                        if (errors.price) setErrors({ ...errors, price: null });
                      }}
                      className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.price ? 'border-red-500' : 'border-gray-300'}`}
                      min="0"
                      step="0.01"
                    />
                    {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
                  </div>
                  <div className="space-y-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Discount %
                    </label>
                    <input
                      type="number"
                      value={discountPercentage}
                      placeholder="Disc %"
                      onChange={(e) => setDiscountPercentage(e.target.value)}
                      className="block w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </div>
                </div>
                {/* Auto Assign Diet Chart */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="autoAssignDietChart"
                    checked={autoAssignDietChart}
                    onChange={(e) => setAutoAssignDietChart(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="autoAssignDietChart" className="text-xs font-medium text-gray-700 cursor-pointer">
                    Auto-Assign Diet Chart to User upon activation
                  </label>
                </div>
                {/* Description */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Description *
                  </label>
                  <textarea
                    value={description}
                    placeholder="Enter description"
                    onChange={(e) => {
                      setDescription(e.target.value);
                      if (errors.description) setErrors({ ...errors, description: null });
                    }}
                    className={`block w-full px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                    rows={2}
                  />
                  {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                </div>
                {/* Features */}
                <div className="space-y-1">
                  <label className="block text-xs font-medium text-gray-700">
                    Features * (One per line)
                  </label>
                  <div className="space-y-2">
                    {features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={feature}
                          placeholder={`Feature ${index + 1}`}
                          onChange={(e) => {
                            handleFeatureChange(index, e.target.value);
                            if (errors.features) setErrors({ ...errors, features: null });
                          }}
                          className={`flex-1 px-2 py-1.5 text-xs border rounded-md text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${errors.features ? 'border-red-500' : 'border-gray-300'}`}
                        />
                        {index === features.length - 1 && (
                          <button
                            type="button"
                            onClick={addFeature}
                            className="px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs font-medium"
                          >
                            Add
                          </button>
                        )}
                        {features.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeFeature(index)}
                            className="px-2 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs font-medium"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 z-10 bg-white border-t border-gray-100 px-6 py-3 rounded-b-2xl flex items-center justify-between">
              <p className="text-xs text-gray-500">Review duration and pricing before saving.</p>
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium shadow"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
